import { MessageType, ParameterType } from './types';
import { mapFromEnum, clone } from '../utils';
var MessageAwareBase = /** @class */ (function () {
    function MessageAwareBase() {
        this._messageListeners = new Map();
        this._messageNameMap = new Map();
        this._parameterNameMap = new Map();
        mapFromEnum(this._messageNameMap, MessageType);
        mapFromEnum(this._parameterNameMap, ParameterType);
    }
    Object.defineProperty(MessageAwareBase.prototype, "messageNames", {
        get: function () {
            return Array.from(this._messageNameMap.values());
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Sets the messge interceptor to call when receiving a message.
     * @param {MessageInterceptor<T>} fn The message interceptor callback.
     */
    MessageAwareBase.prototype.setMessageInterceptor = function (cb) {
        this._messageInterceptor = cb;
    };
    /**
     * Set an event name of the provided `MessageType` type to a desired value.
     * @param {MessageType} type The `MessageType` to override.
     * @param {string} name The name to use for this `MessageType`.
     */
    MessageAwareBase.prototype.setEventName = function (type, name) {
        this._messageNameMap.set(type, name);
    };
    /**
     * Retrieves the configured event name for a message type.
     * @param {MessageType} type The message type.
     */
    MessageAwareBase.prototype.getEventName = function (type) {
        if (!this._messageNameMap.has(type)) {
            throw new Error("An event with type name \"".concat(type, "\" does not exist."));
        }
        return this._messageNameMap.get(type);
    };
    /**
     * Set the name of the provided `ParameterType` type to a desired value.
     * @param {ParameterType} type The `ParameterType` to override.
     * @param {string} name The name to use for this `ParameterType`.
     */
    MessageAwareBase.prototype.setParameterName = function (type, name) {
        this._parameterNameMap.set(type, name);
    };
    /**
     * Retrieves the configured parameter name for a `ParameterType`.
     * @param {ParameterType} type The parameter type.
     */
    MessageAwareBase.prototype.getParameterName = function (type) {
        if (!this._parameterNameMap.has(type)) {
            throw new Error("An parameter with type name \"".concat(type, "\" does not exist."));
        }
        return this._parameterNameMap.get(type);
    };
    /**
     * Retrieves the origin from the location of the current window.
     */
    MessageAwareBase.prototype._getCurrentOrigin = function () {
        return window.location.origin;
    };
    /**
     * Registers a listener for a specific message type that could be dispatched from either the host or embedded context.
     * @param {string} type The message type identifier.
     * @param {} listener The listener to be executed when receiving a message
     */
    MessageAwareBase.prototype.registerMessageListener = function (type, listener) {
        var _this = this;
        var existingListener = this._messageListeners.has(type);
        if (!existingListener) {
            this._messageListeners.set(type, listener);
        }
        if (!this._messageListener) {
            this._messageListener = function (evt) { return _this.handleMessage(evt); };
            window.addEventListener('message', this._messageListener);
        }
    };
    /**
     * Deregisters all message listeners for the provided message type.
     * @param {string} type The message type to remove.
     */
    MessageAwareBase.prototype.deregisterMessageListener = function (type) {
        var listener = this._messageListeners.get(type);
        if (listener) {
            this._messageListeners.delete(type);
        }
    };
    /**
     * Creates a message to be dispathed to either host or embedded context with required parameters.
     * @param {string} type The unique type for this message.
     * @param {T} [data] Optional data value to pass.
     */
    MessageAwareBase.prototype.createMessage = function (type, data) {
        if (!type) {
            throw new Error('Invalid type provided.');
        }
        return {
            hostId: this._hostId,
            type: type,
            data: data
        };
    };
    /**
     * Handles all messages received on the current window.
     * @param {MessageEvent} evt The message event.
     */
    MessageAwareBase.prototype.handleMessage = function (evt) {
        var result = evt.data;
        // If we were provided an interceptor, then call that before continuing the handling of this message
        if (result && this._messageInterceptor && typeof this._messageInterceptor === 'function') {
            var interceptorResult = this._messageInterceptor(clone(result));
            if (interceptorResult.type) {
                result.type = interceptorResult.type;
            }
            if (interceptorResult.hostId) {
                result.hostId = interceptorResult.hostId;
            }
        }
        // Make sure the message follows the proper data contract
        if (!result || !result.type) {
            return;
        }
        // Make sure received the message from our host context origin only
        if (this._origin && evt.origin !== this._origin) {
            console.warn("Message received from unexpected origin: ".concat(evt.origin));
            return;
        }
        // Make sure the host id that is provided matches the one we have
        if (this._hostId && result.hostId !== this._hostId) {
            console.warn("Message received has an invalid host id: ".concat(result.hostId));
            return;
        }
        var listener = this._messageListeners.get(result.type);
        if (!listener) {
            return;
        }
        if (typeof listener === 'function') {
            listener(clone(result));
        }
    };
    return MessageAwareBase;
}());
export { MessageAwareBase };
